import React from 'react';
import { ViewState, User } from '../types';
import { LayoutDashboard, PlusCircle, Users, Settings, LogOut, Menu, X, Plus } from 'lucide-react';

interface LayoutProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  user: User;
  onLogout: () => void;
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ currentView, setView, user, onLogout, children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const NavItem = ({ view, label, icon: Icon }: { view: ViewState; label: string; icon: any }) => (
    <button
      onClick={() => {
        setView(view);
        setMobileMenuOpen(false);
      }}
      className={`flex items-center w-full px-4 py-3 text-sm font-medium transition-colors rounded-lg ${
        currentView === view
          ? 'bg-red-800 text-white shadow-md'
          : 'text-gray-600 hover:bg-red-50 hover:text-red-700'
      }`}
    >
      <Icon className="w-5 h-5 mr-3" />
      {label}
    </button>
  );

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-gray-200">
        <div className="flex items-center justify-center h-20 border-b border-gray-200 bg-red-900">
          <h1 className="text-xl font-bold text-white tracking-wide">Brittner's Smokehouse</h1>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-2">
          <NavItem view="DASHBOARD" label="Dashboard" icon={LayoutDashboard} />
          <NavItem view="NEW_ORDER" label="New Order" icon={PlusCircle} />
          <NavItem view="CUSTOMERS" label="Customers" icon={Users} />
          <NavItem view="ADMIN" label="Admin" icon={Settings} />
        </nav>

        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center mb-4 px-2">
            {user.imageUrl ? (
              <img src={user.imageUrl} alt={user.name} className="w-8 h-8 rounded-full" />
            ) : (
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-800 font-bold">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-700">Logged in as</p>
              <p className="text-xs text-gray-500">{user.name}</p>
              <p className="text-xs text-gray-400">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onLogout}
            className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden flex flex-col flex-1 h-screen">
         <header className="flex items-center justify-between h-16 px-4 bg-red-900 text-white shadow-md z-20">
            <div className="flex items-center">
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="mr-3">
                  {mobileMenuOpen ? <X /> : <Menu />}
                </button>
                <span className="font-bold text-lg truncate">Brittner's Smokehouse</span>
            </div>

            <button 
                onClick={() => setView('NEW_ORDER')}
                className="bg-white/10 p-2 rounded-full hover:bg-white/20 active:bg-white/30 transition-colors"
                title="Quick Add Order"
            >
                <Plus className="w-6 h-6 text-white" />
            </button>
         </header>

         {/* Mobile Menu Overlay */}
         {mobileMenuOpen && (
           <div className="absolute top-16 left-0 w-full bg-white shadow-xl z-30 border-b border-gray-200 p-4 space-y-2">
              <NavItem view="DASHBOARD" label="Dashboard" icon={LayoutDashboard} />
              <NavItem view="NEW_ORDER" label="New Order" icon={PlusCircle} />
              <NavItem view="CUSTOMERS" label="Customers" icon={Users} />
              <NavItem view="ADMIN" label="Admin" icon={Settings} />
              <button
                onClick={onLogout}
                className="flex items-center w-full px-4 py-3 text-sm font-medium text-red-600 border-t border-gray-100 mt-2 pt-4"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </button>
           </div>
         )}

         <main className="flex-1 overflow-auto p-4 md:p-8">
            {children}
         </main>
      </div>

      {/* Desktop Main Content */}
      <main className="hidden md:block flex-1 overflow-auto p-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
